// "New project" (RF-PROJ-01, RF-UI-04, D36 "simple first, advanced on demand"). The dialog first offers two choices:
// "Import a GitHub repository" (the existing from-GitHub onboarding: POST /v1/projects/from-github with a repository
// of the organization's connected GitHub integration) and "Blank project" (POST /v1/projects). A blank project only
// needs a name; repository URL, base branch, delivery target and autonomy preset live under a collapsed "Advanced
// settings" section with the same defaults as before (main, pull request, observation). One Idempotency-Key per opened
// dialog (honoured by both endpoints), so a retry after a timeout never creates a second project.
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { AUTONOMY_PRESETS, DELIVERY_TARGETS } from "../lib/projectOptions.ts";
import type { AutonomyPreset, DeliveryTarget } from "@maestros/contracts";
import { useI18n } from "../i18n/index.tsx";
import { createProject } from "../api/endpoints.ts";
import { createProjectFromGithub } from "../api/endpoints-onboarding.ts";
import { isApiError } from "../api/client.ts";
import { newId } from "../lib/storage.ts";
import { Dialog } from "../components/Dialog.tsx";
import { Button } from "../components/Button.tsx";
import { SelectField, TextAreaField, TextField } from "../components/Field.tsx";
import { Banner, localErrorMessage } from "../components/Feedback.tsx";
import { ActionError } from "../components/ActionError.tsx";
import { useToast } from "../components/Toast.tsx";
import { GithubMark } from "../components/GithubMark.tsx";
import { ChevronDownIcon, ChevronRightIcon, FileTextIcon } from "../components/icons.tsx";
import { useOrgRole } from "./config/shared.tsx";
import { GithubRepositoryPicker, useGithubPicker } from "./onboarding/GithubRepositoryPicker.tsx";

export type CreateProjectStep = "choose" | "github" | "blank";

export const PROJECT_DEFAULTS = { baseBranch: "main", deliveryTarget: "pull_request" as DeliveryTarget, autonomy: "observation" as AutonomyPreset };

function Choice({ icon, title, hint, onSelect, disabled, testId, autoFocus }: { icon: ReactNode; title: string; hint: string; onSelect: () => void; disabled?: boolean; testId: string; autoFocus?: boolean }) {
  const hintId = useId();
  return (
    <button type="button" className="project-choice" onClick={onSelect} disabled={disabled} aria-describedby={hintId} data-testid={testId} {...(autoFocus ? { "data-step-focus": "" } : {})}>
      <span className="project-choice-icon">{icon}</span>
      <span className="project-choice-text">
        <span className="project-choice-title">{title}</span>
        <span id={hintId} className="project-choice-hint">
          {hint}
        </span>
      </span>
      <ChevronRightIcon size={18} className="project-choice-chevron" />
    </button>
  );
}

export function CreateProjectDialog({ open, onClose, mode = "new" }: { open: boolean; onClose: () => void; mode?: "new" | "import" | "github" }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { toast } = useToast();
  const canImport = useOrgRole().atLeast("admin");
  // "import" (Home suggestion) and "github" (Projects page) open straight on the GitHub import (admins only).
  const initialStep: CreateProjectStep = mode !== "new" && canImport ? "github" : "choose";
  const [step, setStep] = useState<CreateProjectStep>(initialStep);
  const [key, setKey] = useState(newId);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [repositoryUrl, setRepositoryUrl] = useState("");
  const [baseBranch, setBaseBranch] = useState(PROJECT_DEFAULTS.baseBranch);
  const [deliveryTarget, setDeliveryTarget] = useState<DeliveryTarget>(PROJECT_DEFAULTS.deliveryTarget);
  const [autonomy, setAutonomy] = useState<AutonomyPreset>(PROJECT_DEFAULTS.autonomy);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const picker = useGithubPicker(open && step === "github");
  const bodyRef = useRef<HTMLDivElement>(null);
  const advancedId = useId();

  useEffect(() => {
    if (open) {
      setStep(initialStep);
      setKey(newId());
      setErrors({});
      setSubmitError(null);
      setAdvanced(false);
      focusStep();
    }
    // The role arrives with the session: an "import" opened before it is known lands on the GitHub step once it is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canImport]);

  // Opening, and moving between steps, puts focus on the step's first control (not on the dialog's close button).
  function focusStep() {
    requestAnimationFrame(() => {
      const el = bodyRef.current?.querySelector<HTMLElement>("[data-step-focus], input:not([disabled]), select, textarea, button:not([disabled])");
      el?.focus();
    });
  }
  const go = (next: CreateProjectStep) => {
    setStep(next);
    setSubmitError(null);
    setErrors({});
    focusStep();
  };

  const reset = () => {
    setName("");
    setDescription("");
    setRepositoryUrl("");
    setBaseBranch(PROJECT_DEFAULTS.baseBranch);
    setDeliveryTarget(PROJECT_DEFAULTS.deliveryTarget);
    setAutonomy(PROJECT_DEFAULTS.autonomy);
  };

  const submitBlank = async () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next["name"] = t("validation.required");
    if (repositoryUrl.trim() && !/^(https?:\/\/|git@|ssh:\/\/)\S+$/.test(repositoryUrl.trim())) next["repository"] = t("validation.repository");
    setErrors(next);
    if (next["repository"]) setAdvanced(true);
    if (Object.keys(next).length > 0) return;
    const project = await createProject(
      {
        name: name.trim(),
        description: description.trim(),
        repository_url: repositoryUrl.trim() || null,
        base_branch: baseBranch.trim() || PROJECT_DEFAULTS.baseBranch,
        delivery_target: deliveryTarget,
        autonomy_preset: autonomy,
      },
      key,
    );
    toast(t("createProject.created", { name: project.name }), "success");
    onClose();
    reset();
    navigate(`/projects/${project.id}`);
  };

  const submitGithub = async () => {
    const { connection, repository } = picker;
    if (!connection || !repository) return;
    const created = await createProjectFromGithub({ integration_id: connection.id, repository: repository.fullName }, key);
    toast(t("githubOnboarding.created", { name: created.project.name }), "success");
    onClose();
    navigate(`/projects/${created.project.id}/settings`);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (step === "choose") return;
    setBusy(true);
    setSubmitError(null);
    try {
      await (step === "github" ? submitGithub() : submitBlank());
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  const formId = "create-project-form";
  const title = step === "github" ? t("githubOnboarding.newFromGithub") : step === "blank" ? t("createProject.blank") : t("createProject.title");
  const description_ = step === "github" ? t("githubOnboarding.createDescription") : step === "blank" ? t("createProject.blankDescription") : t("createProject.chooseDescription");
  const retryable = isApiError(submitError) && submitError.retryable;
  const canGoBack = step !== "choose";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={busy}
      testId="create-project-dialog"
      title={title}
      description={description_}
      footer={
        step === "choose" ? (
          <Button onClick={onClose}>{t("common.cancel")}</Button>
        ) : (
          <>
            {canGoBack ? (
              <Button variant="ghost" onClick={() => go("choose")} disabled={busy} className="dialog-footer-back">
                {t("common.back")}
              </Button>
            ) : null}
            <Button onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form={formId} variant="primary" loading={busy} disabled={step === "github" && !picker.repository}>
              {retryable ? t("common.retry") : t("createProject.submit")}
            </Button>
          </>
        )
      }
    >
      <div ref={bodyRef} className="stack">
        {step === "choose" ? (
          <div role="group" aria-label={t("createProject.choiceLabel")} className="project-choices">
            <Choice
              testId="choice-github"
              icon={<GithubMark size={24} />}
              title={t("createProject.fromGithub")}
              hint={canImport ? t("createProject.fromGithubHint") : t("createProject.fromGithubAdminOnly")}
              onSelect={() => go("github")}
              disabled={!canImport}
              autoFocus={canImport}
            />
            <Choice testId="choice-blank" autoFocus={!canImport} icon={<FileTextIcon size={24} />} title={t("createProject.blank")} hint={t("createProject.blankHint")} onSelect={() => go("blank")} />
          </div>
        ) : (
          <form id={formId} className="stack" onSubmit={onSubmit} noValidate>
            {submitError ? (
              step === "github" ? (
                <ActionError error={submitError} />
              ) : (
                <Banner tone="error" role="alert" title={t("createProject.failed")}>
                  {localErrorMessage(submitError, t)}
                  {retryable ? <p className="field-hint">{t("common.retrySafe")}</p> : null}
                </Banner>
              )
            ) : null}
            {step === "github" ? (
              <GithubRepositoryPicker picker={picker} onLeave={onClose} />
            ) : (
              <>
                <TextField
                  label={t("project.name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  error={errors["name"] ?? null}
                  placeholder={t("createProject.nameExample")}
                  required
                  data-step-focus=""
                />
                <TextAreaField label={t("project.description")} optionalLabel={t("common.optional")} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={3} />
                <div className="advanced-settings">
                  <button type="button" className="advanced-toggle" aria-expanded={advanced} aria-controls={advancedId} onClick={() => setAdvanced((v) => !v)}>
                    {advanced ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
                    <span>{t("createProject.advanced")}</span>
                  </button>
                  {!advanced ? (
                    <p className="field-hint advanced-summary">
                      {t("createProject.advancedSummary", { branch: baseBranch.trim() || PROJECT_DEFAULTS.baseBranch, delivery: t(`delivery.${deliveryTarget}`), autonomy: t(`autonomy.${autonomy}`) })}
                    </p>
                  ) : null}
                  <div id={advancedId} className="stack advanced-body" hidden={!advanced}>
                    <TextField
                      label={t("project.repository")}
                      optionalLabel={t("common.optional")}
                      value={repositoryUrl}
                      onChange={(e) => setRepositoryUrl(e.target.value)}
                      placeholder="https://github.com/org/repo.git"
                      error={errors["repository"] ?? null}
                      hint={t("createProject.repositoryHint")}
                      spellCheck={false}
                    />
                    <div className="form-grid">
                      <TextField label={t("project.baseBranch")} value={baseBranch} onChange={(e) => setBaseBranch(e.target.value)} maxLength={200} spellCheck={false} />
                      <SelectField label={t("project.deliveryTarget")} value={deliveryTarget} onChange={(e) => setDeliveryTarget(e.target.value as DeliveryTarget)}>
                        {DELIVERY_TARGETS.map((d) => (
                          <option key={d} value={d}>
                            {t(`delivery.${d}`)}
                          </option>
                        ))}
                      </SelectField>
                    </div>
                    <SelectField label={t("project.autonomy")} value={autonomy} onChange={(e) => setAutonomy(e.target.value as AutonomyPreset)} hint={t("createProject.autonomyHint")}>
                      {AUTONOMY_PRESETS.map((a) => (
                        <option key={a} value={a}>
                          {t(`autonomy.${a}`)}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </Dialog>
  );
}
